import { cloneDeep, merge, pick } from 'lodash-es';
import {
  createAdminFields,
  createGroupFields,
} from '@/ccp-mfe/components/ccp-details/ccp-employees-table/ccp-employees-table-fields.config';
import { fetchFieldsConfig } from '@/ccp-mfe/query/ccp-details';
import {
  type AdminOrRoleId,
  BACKEND_DATA_DISPLAY_TYPE_MAP,
  type BackendDataDisplayTypeKey,
  type BaseCol,
  DEFAULT_CURRENCY_DISPLAY,
  type DefaultFieldName,
  type DisplayLayout,
  FIELD_TYPE_WIDTH_MAP,
  TRUNCATE_OPTIONS,
} from '@/ccp-mfe/types';
import { createEmptyFilterState } from '@/ccp-mfe/utils/table';
import { useAuthStore } from '@/stores/auth';

/**
 * Base column settings
 *
 * @constant
 * @type {Object}
 * @property {string} name - Field name
 * @property {string} title - Field title, gets displayed in table head
 * @property {string} valueLabel - Value label, gets displayed in table row cell as label for compound fields
 * @property {boolean} filterable - Whether the column is filterable or not
 * @property {Object} filterState - The filter state object for this column. Holds the selected values of the filters.
 * @property {Object} filterMinMax - Whether the column has a min/max filter
 * @property {boolean} sortable - Whether we should display sorting or not
 * @property {null | FieldType} type - The field type
 * @property {Object} truncateOptions - The truncate options for this column
 * @property {null | string } emptyText - Whether the field has a custom text when it's empty
 * @property {null | Object} style - Applies custom styles to table headers (used mainly for setting max-width)
 * @property {null | Object} contentStyle - Applies custom styles to table cells
 * @property {boolean} editable - Whether the column is editable or not
 * @property {number} id - The layout (field) id
 * @property {number} decimalPrecision - Decimal precision for the formatting of the table cell
 * @property {string} borderColor - A hex color code for the border color of the table header cell
 * @property {string} thAttrs - Attributes that get applied to the table's `th` element
 * @property {string} headerCellAttrs - Attributes that get applied to the table's header cell component
 * @property {object} filterListProps - Properties for the filterList component
 * @property {string} filterActive - Whether the filter is active or not
 * @property {BaseCol['name'][]} compoundLayouts - Child column names
 * @property {boolean} hidden - Whether that field is excluded from rendering
 * @property {string} contentTemplate - Custom template for the cell contents
 * @property {string} tooltip - Tooltip for the column
 * @property {string} syncTooltip - Tooltip for the sync icon
 * @property {EmployeeV2DisplayLayout | null} syncLayout - The employee v2 display layout that the column is synced with
 * @property {CurrencyDisplay} currencyDisplay - The currency display for the column
 */
const baseCol = {
  name: '',
  data_name: '',
  title: '',
  valueLabel: '',
  filterable: false,
  filterState: createEmptyFilterState(),
  filterMinMax: false,
  sortable: false,
  type: 'text',
  truncateOptions: TRUNCATE_OPTIONS,
  emptyText: null,
  style: {},
  contentStyle: {},
  editable: true,
  id: 0,
  decimalPrecision: 0,
  borderColor: '',
  thAttrs: {},
  headerCellAttrs: {},
  filterListProps: {},
  filterActive: false,
  compoundLayouts: [],
  hidden: false,
  contentTemplate: '',
  tooltip: '',
  syncTooltip: '',
  syncLayout: null,
  currencyDisplay: DEFAULT_CURRENCY_DISPLAY,
  isAvatarVisible: true,
} satisfies BaseCol;

export type Col = BaseCol;

/**
 * Options for all the default columns.
 */
const defaultCols = [
  { name: 'first_name' },
  { name: 'co_employee_id' },
  { name: 'start_date', type: 'date' },
  { name: 'gender' },
  { name: 'race_ethnicity' },
  { name: 'job_family_group' },
  { name: 'job_family' },
  { name: 'job_title' },
  { name: 'level' },
  { name: 'location' },
  { name: 'geo' },
  { name: 'region' },
  { name: 'local_currency' },
  { name: 'notes' },
  { name: 'eligible', type: 'boolean' },
  { name: 'promotion', type: 'boolean' },
  { name: 'new_level' },
  { name: 'new_job_title' },
  { name: 'rating' },
  { name: 'current_salary', type: 'currency' },
  { name: 'salary_increase', type: 'inputCurrency' },
  { name: 'New Salary' },
  { name: 'New Compa Ratio' },
  { name: 'annual_bonus', type: 'inputCurrency' },
  { name: 'spot_bonus', type: 'inputCurrency' },
  { name: 'promo_equity', type: 'inputCurrency' },
  { name: 'refresh_equity', type: 'inputCurrency' },
  { name: 'spot_equity', type: 'inputCurrency' },
] satisfies Partial<Col & { name: DefaultFieldName }>[];

const createCols = (backendCols: DisplayLayout[], isAvatarVisible: boolean): Col[] => {
  const authStore = useAuthStore();

  const res = backendCols
    .map((bCol) => {
      // Discard `type` from the backend config for now, every field will inherit `type` "text" by default.
      // Discard other display layout attrs that are not needed or need to be renamed for columns/cells rendering.
      const backendCol = pick(bCol, [
        'id',
        'name',
        'data_name',
        'title',
        'sortable',
        'filterable',
        'hidden',
        'editable',
        'tooltip',
      ]) as Col;
      backendCol.title = bCol?.compound_layouts.length > 0 ? bCol.compound_group_name || bCol.title : bCol.title;
      backendCol.syncTooltip = bCol.employee_v2_display_layout?.id
        ? `Last synced from People data column: ${bCol.employee_v2_display_layout.display_name}`
        : '';
      backendCol.syncLayout = bCol.employee_v2_display_layout;
      backendCol.valueLabel = bCol.title;
      backendCol.decimalPrecision = bCol.decimal_precision || 0;
      backendCol.borderColor = bCol.border_color;
      backendCol.filterMinMax = bCol.filter_min_max;
      backendCol.truncateOptions = bCol.truncate_options;
      backendCol.style = createColStyle(bCol.name, bCol?.width, bCol.type);
      backendCol.contentStyle = createColStyle(bCol.name, bCol?.width, bCol.type);
      backendCol.emptyText = bCol.empty_text;
      backendCol.type = bCol.is_list === true ? 'selectText' : BACKEND_DATA_DISPLAY_TYPE_MAP[bCol.type];
      backendCol.compoundLayouts = bCol?.compound_layouts.length
        ? createCols(bCol?.compound_layouts, isAvatarVisible)
        : [];
      backendCol.editable = bCol.editable || !!bCol?.compound_layouts?.find?.((layout) => layout.editable);
      backendCol.contentTemplate = bCol.content_template;
      backendCol.currencyDisplay = bCol.currency_display;
      backendCol.isAvatarVisible = isAvatarVisible;
      if (authStore.isUserStaff) {
        backendCol.thAttrs = { title: bCol.name };
      }

      let col = {};
      const defaultCol = defaultCols.find((defaultCol) => defaultCol.name === backendCol.name);

      if (defaultCol) {
        // Default field
        if (defaultCol.type) {
          backendCol.type = defaultCol.type;
        }

        col = merge(cloneDeep(baseCol), defaultCol, backendCol);
      } else {
        // Custom field
        if (backendCol.type === 'date') {
          backendCol.emptyText = 'Add Date';
        }
        col = merge(cloneDeep(baseCol), backendCol);
      }

      return col as Col;
    })
    .filter((col) => !col.hidden);

  return res as Col[];
};

const getColsConfig = async (
  cycleId: number,
  roleId: AdminOrRoleId,
  planningGroupId: number | null,
  convertTo?: string
) => {
  if (!cycleId) {
    throw Error('You need to provide a cycle id.');
  }

  if (!roleId) {
    throw Error('You need to provide a role id.');
  }

  const backendCols = await fetchFieldsConfig(cycleId, {
    planning_role_id: roleId,
    planning_group_id: planningGroupId,
    layout_name: 'employees',
    convert_to: convertTo,
  });

  return backendCols;
};

export const createAdminColsAndFields = async (
  opts: { cycleId: number; isAvatarVisible: boolean; convertTo?: string } | Record<string, never> = {}
) => {
  if (!opts.cycleId) {
    throw Error('You need to provide a cycleId.');
  }

  const backendCols = await getColsConfig(opts.cycleId, 'admin', null, opts.convertTo);
  const cols = createCols(backendCols, opts.isAvatarVisible);
  const fields = createAdminFields(backendCols);

  return { cols, fields };
};

export const createGroupColsAndFields = async (
  opts:
    | {
        cycleId: number;
        roleId: AdminOrRoleId;
        planningGroupId: number | null;
        isAvatarVisible: boolean;
        convertTo?: string;
      }
    | Record<string, never> = {}
) => {
  if (!opts.cycleId) {
    throw Error('You need to provide a cycleId.');
  }

  if (!opts.planningGroupId) {
    throw Error('You need to provide a planningGroupId.');
  }

  const backendCols = await getColsConfig(opts.cycleId, opts.roleId, opts.planningGroupId, opts.convertTo);
  const cols = createCols(backendCols, opts.isAvatarVisible).map((col) => {
    // For planners we need to set the emptyText to "—" for all fields
    col.emptyText = col.emptyText ? '—' : null;

    return col;
  });
  const fields = createGroupFields(backendCols);

  return { cols, fields };
};

const createColStyle = (colName: string, colWidth: string | null, colDisplayType: BackendDataDisplayTypeKey) => {
  if (['first_name'].includes(colName)) {
    return {};
  }

  const width = colWidth ? `${parseInt(colWidth)}px` : FIELD_TYPE_WIDTH_MAP[colDisplayType];

  return { 'max-width': width, 'min-width': width } as Record<string, string>;
};
